/**
 * Admin panel — DIGIPIN / service API access requests, approval, key lifecycle.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyIcon from '@mui/icons-material/Key';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import Header from '../Header';
import {
  adminGetDigipinStats,
  adminListDigipinKeys,
  adminListDigipinRequests,
  adminReviewDigipinRequest,
  adminRevokeDigipinKey,
  adminReissueDigipinKey,
  DIGIPIN_CLOUD_API_URL,
  type DigipinKeyMeta,
  type DigipinRequest,
  type DigipinAdminStats,
} from '../../services/digipinAccessService';
import { useAppSelector } from '../../store/hooks';
import { Navigate, Link as RouterLink } from 'react-router-dom';

type RequestRow = DigipinRequest & { user_email?: string; user_id?: string };

/** One row per user — backend already dedupes; this is a safety net for display. */
function uniqueByUser<T extends { user_id?: string; user_email?: string; id?: string }>(
  rows: T[]
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    const key = (row.user_id || row.user_email || row.id || '').toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

const statusChipSx = (status?: string) => {
  if (status === 'pending') {
    return {
      bgcolor: 'rgba(255,152,0,0.12)',
      color: '#ffb74d',
      border: '1px solid rgba(255,152,0,0.25)',
      fontWeight: 600,
    };
  }
  if (status === 'approved' || status === 'active') {
    return {
      bgcolor: 'rgba(76,175,80,0.12)',
      color: '#81c784',
      border: '1px solid rgba(76,175,80,0.25)',
      fontWeight: 600,
    };
  }
  if (status === 'rejected' || status === 'revoked') {
    return {
      bgcolor: 'rgba(244,67,54,0.12)',
      color: '#e57373',
      border: '1px solid rgba(244,67,54,0.25)',
      fontWeight: 600,
    };
  }
  return {
    bgcolor: 'rgba(255,255,255,0.08)',
    color: '#aec7b3',
    border: '1px solid rgba(255,255,255,0.15)',
    fontWeight: 600,
  };
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#edf5e8',
    backgroundColor: 'rgba(7,22,18,0.45)',
    '& fieldset': { borderColor: 'rgba(215,245,173,.18)' },
    '&:hover fieldset': { borderColor: 'rgba(215,245,173,.32)' },
    '&.Mui-focused fieldset': { borderColor: '#d7f5ad' },
  },
  '& .MuiInputLabel-root': {
    color: '#aec7b3',
    '&.Mui-focused': { color: '#d7f5ad' },
  },
  '& .MuiFormHelperText-root': { color: '#889e92' },
};

const DigipinApiKeysAdminPage = () => {
  const { user, isAuthenticated } = useAppSelector((s) => s.auth);
  const [tab, setTab] = useState(0);
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [keys, setKeys] = useState<DigipinKeyMeta[]>([]);
  const [stats, setStats] = useState<DigipinAdminStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [issuedMeta, setIssuedMeta] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [requestFilter, setRequestFilter] = useState<'all' | 'pending'>('pending');
  /** Keys list: active only (one per user) vs every user’s latest key including revoked */
  const [keyFilter, setKeyFilter] = useState<'active' | 'all'>('active');

  const load = useCallback(async () => {
    await Promise.resolve();
    setError(null);
    setLoading(true);
    try {
      const statusQ = requestFilter === 'pending' ? 'pending' : undefined;
      const keyStatusQ = keyFilter === 'active' ? 'active' : undefined;
      const [r, k, s] = await Promise.all([
        adminListDigipinRequests(statusQ),
        adminListDigipinKeys(keyStatusQ),
        adminGetDigipinStats(),
      ]);
      setRequests(uniqueByUser(r.requests || []));
      setKeys(uniqueByUser(k.keys || []));
      setStats(s.stats || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [requestFilter, keyFilter]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  const pendingRequests = useMemo(() => requests.filter((r) => r.status === 'pending'), [requests]);
  const uniqueUserCount = useMemo(
    () =>
      new Set(keys.map((k) => (k.user_email || k.user_id || '').toLowerCase()).filter(Boolean))
        .size,
    [keys]
  );

  if (!isAuthenticated) {
    return <Navigate to="/login?redirect=%2Fadmin%2Fapi-keys" replace />;
  }

  if (user && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const review = async (id: string, action: 'approve' | 'reject') => {
    setBusyId(id);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminReviewDigipinRequest(id, {
        action,
        adminNote: note || undefined,
      });
      if (result.key) {
        setIssuedKey(result.key);
        setIssuedMeta(result.keyPrefix || null);
      }
      setNote('');
      setSuccess(
        action === 'approve'
          ? result.emailSent
            ? `Request approved. Approval email sent to ${result.userEmail || 'the requester'}. Copy the key (they can also claim it once).`
            : result.emailMock
              ? 'Request approved. Email is in mock mode (set SMTP_* on the backend to deliver mail). Copy the key for the user.'
              : 'Request approved. Copy the key (user can also claim it once). Approval email attempted if SMTP is configured.'
          : 'Request rejected.'
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Review failed');
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (id: string) => {
    if (!window.confirm('Revoke this API key? QGIS online mode will stop for this user.')) {
      return;
    }
    setBusyId(id);
    setError(null);
    try {
      await adminRevokeDigipinKey(id);
      setSuccess('Key revoked.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  const reissue = async (userId: string, email?: string) => {
    if (
      !window.confirm(
        `Re-issue a new DIGIPIN API key for ${email || userId}? Any active keys will be revoked.`
      )
    ) {
      return;
    }
    setBusyId(userId);
    setError(null);
    try {
      const result = await adminReissueDigipinKey(userId, {
        adminNote: note || 'Key re-issued by admin',
      });
      if (result.key) {
        setIssuedKey(result.key);
        setIssuedMeta(result.keyPrefix || null);
      }
      setSuccess('New key issued.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reissue failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        background: 'linear-gradient(180deg, #102820 0%, #0b201b 45%, #081713 100%)',
        color: '#eaf2df',
        overflow: 'auto',
      }}
    >
      <Header subtitle="Admin" active="admin" />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(90deg, #fff 0%, #d7f5ad 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5,
              }}
            >
              Service & DIGIPIN admin
            </Typography>
            <Typography sx={{ color: '#aec7b3' }}>
              Review access requests, issue keys for QGIS, revoke or re-issue. Cloud API:{' '}
              <Box
                component="code"
                sx={{
                  color: '#d7f5ad',
                  bgcolor: 'rgba(215,245,173,0.1)',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                }}
              >
                {DIGIPIN_CLOUD_API_URL}
              </Box>
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={
              loading ? <CircularProgress size={16} sx={{ color: '#d7f5ad' }} /> : <RefreshIcon />
            }
            onClick={() => load()}
            disabled={loading}
            sx={{
              borderColor: 'rgba(215,245,173,.35)',
              color: '#edf5e8',
              borderRadius: 0.5,
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': {
                borderColor: '#d7f5ad',
                bgcolor: 'rgba(215,245,173,.08)',
              },
              '&.Mui-disabled': {
                borderColor: 'rgba(215,245,173,.1)',
                color: 'rgba(237,245,232,.3)',
              },
            }}
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Stats */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Card
            sx={{
              flex: 1,
              bgcolor: 'rgba(16, 41, 35, 0.45)',
              border: '1px solid rgba(215, 245, 173, 0.15)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <PendingActionsIcon sx={{ color: '#ffa726', fontSize: 28 }} />
                <Box>
                  <Typography variant="h4" fontWeight={800} sx={{ color: '#edf5e8' }}>
                    {stats?.pendingCount ?? pendingRequests.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#b8cabd' }}>
                    Pending requests
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          <Card
            sx={{
              flex: 1,
              bgcolor: 'rgba(16, 41, 35, 0.45)',
              border: '1px solid rgba(215, 245, 173, 0.15)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <KeyIcon sx={{ color: '#81c784', fontSize: 28 }} />
                <Box>
                  <Typography variant="h4" fontWeight={800} sx={{ color: '#edf5e8' }}>
                    {stats?.activeKeys ?? keys.filter((k) => k.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#b8cabd' }}>
                    Active API keys
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          <Card
            sx={{
              flex: 1,
              bgcolor: 'rgba(16, 41, 35, 0.45)',
              border: '1px solid rgba(215, 245, 173, 0.15)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <CheckCircleIcon sx={{ color: '#d7f5ad', fontSize: 28 }} />
                <Box>
                  <Typography variant="h4" fontWeight={800} sx={{ color: '#edf5e8' }}>
                    {stats?.callsToday ?? 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#b8cabd' }}>
                    DIGIPIN calls today (UTC)
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Paper
          sx={{
            p: 2,
            mb: 2,
            bgcolor: 'rgba(16, 41, 35, 0.45)',
            border: '1px solid rgba(215, 245, 173, 0.15)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            borderRadius: 2,
          }}
        >
          <TextField
            label="Admin note (optional — stored with approve / reject / re-issue)"
            fullWidth
            size="small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Approved for survey education pilot"
            sx={fieldSx}
          />
        </Paper>

        <Paper
          sx={{
            mb: 3,
            bgcolor: 'rgba(16, 41, 35, 0.45)',
            border: '1px solid rgba(215, 245, 173, 0.15)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              px: 1,
              borderBottom: 1,
              borderColor: 'rgba(215,245,173,0.15)',
              '& .MuiTabs-indicator': {
                backgroundColor: '#d7f5ad',
              },
            }}
          >
            <Tab
              label={`Requests (${requestFilter === 'pending' ? pendingRequests.length : requests.length})`}
              sx={{
                color: '#aec7b3',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 14,
                '&.Mui-selected': {
                  color: '#d7f5ad',
                  fontWeight: 800,
                },
              }}
            />
            <Tab
              label={`Keys (${uniqueUserCount || keys.length})`}
              sx={{
                color: '#aec7b3',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 14,
                '&.Mui-selected': {
                  color: '#d7f5ad',
                  fontWeight: 800,
                },
              }}
            />
            <Tab
              label="Workflow"
              sx={{
                color: '#aec7b3',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 14,
                '&.Mui-selected': {
                  color: '#d7f5ad',
                  fontWeight: 800,
                },
              }}
            />
          </Tabs>

          {tab === 0 && (
            <Box sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip
                  label="Pending only"
                  color={requestFilter === 'pending' ? 'warning' : 'default'}
                  onClick={() => setRequestFilter('pending')}
                  clickable
                  sx={{
                    bgcolor:
                      requestFilter === 'pending'
                        ? 'rgba(255,152,0,0.12)'
                        : 'rgba(255,255,255,0.08)',
                    color: requestFilter === 'pending' ? '#ffb74d' : '#aec7b3',
                    border:
                      requestFilter === 'pending'
                        ? '1px solid rgba(255,152,0,0.25)'
                        : '1px solid rgba(255,255,255,0.15)',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label="All requests"
                  color={requestFilter === 'all' ? 'primary' : 'default'}
                  onClick={() => setRequestFilter('all')}
                  clickable
                  sx={{
                    bgcolor:
                      requestFilter === 'all' ? 'rgba(215,245,173,0.12)' : 'rgba(255,255,255,0.08)',
                    color: requestFilter === 'all' ? '#d7f5ad' : '#aec7b3',
                    border:
                      requestFilter === 'all'
                        ? '1px solid rgba(215,245,173,0.25)'
                        : '1px solid rgba(255,255,255,0.15)',
                    fontWeight: 600,
                  }}
                />
              </Stack>
              {loading ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <CircularProgress size={28} sx={{ color: '#d7f5ad' }} />
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ borderColor: 'rgba(215,245,173,0.15)' }}>
                        <TableCell
                          sx={{
                            color: '#d7f5ad',
                            fontWeight: 700,
                            borderColor: 'rgba(215,245,173,0.15)',
                          }}
                        >
                          User
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#d7f5ad',
                            fontWeight: 700,
                            borderColor: 'rgba(215,245,173,0.15)',
                          }}
                        >
                          Use case
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#d7f5ad',
                            fontWeight: 700,
                            borderColor: 'rgba(215,245,173,0.15)',
                          }}
                        >
                          Org / volume
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#d7f5ad',
                            fontWeight: 700,
                            borderColor: 'rgba(215,245,173,0.15)',
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#d7f5ad',
                            fontWeight: 700,
                            borderColor: 'rgba(215,245,173,0.15)',
                          }}
                        >
                          Submitted
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: '#d7f5ad',
                            fontWeight: 700,
                            borderColor: 'rgba(215,245,173,0.15)',
                          }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.map((r) => (
                        <TableRow
                          key={r.id}
                          hover
                          sx={{ '&:hover': { bgcolor: 'rgba(215,245,173,0.04) !important' } }}
                        >
                          <TableCell sx={{ borderColor: 'rgba(215,245,173,0.15)' }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#edf5e8' }}>
                              {r.user_email || '—'}
                            </Typography>
                            {(r.request_count ?? 0) > 1 && (
                              <Typography
                                variant="caption"
                                sx={{ color: '#889e92' }}
                                display="block"
                              >
                                {r.request_count} total submissions (showing latest)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280, borderColor: 'rgba(215,245,173,0.15)' }}>
                            <Typography variant="body2" sx={{ color: '#edf5e8' }}>
                              {r.use_case || r.useCase || '—'}
                            </Typography>
                            {r.admin_note && (
                              <Typography
                                variant="caption"
                                sx={{ color: '#aec7b3' }}
                                display="block"
                              >
                                Note: {r.admin_note}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(215,245,173,0.15)' }}>
                            <Typography variant="body2" sx={{ color: '#edf5e8' }}>
                              {r.organization || '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#aec7b3' }}>
                              {r.estimated_monthly_calls != null
                                ? `${r.estimated_monthly_calls}/mo`
                                : ''}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: 'rgba(215,245,173,0.15)' }}>
                            <Chip size="small" label={r.status} sx={statusChipSx(r.status)} />
                          </TableCell>
                          <TableCell
                            sx={{ borderColor: 'rgba(215,245,173,0.15)', color: '#b8cabd' }}
                          >
                            {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ borderColor: 'rgba(215,245,173,0.15)' }}>
                            {r.status === 'pending' ? (
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Tooltip title="Issue API key + notify via claim UI">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      startIcon={<CheckCircleIcon />}
                                      disabled={busyId === r.id}
                                      onClick={() => review(r.id, 'approve')}
                                      sx={{
                                        bgcolor: '#2e7d32',
                                        color: '#fff',
                                        fontWeight: 700,
                                        '&:hover': { bgcolor: '#1b5e20' },
                                      }}
                                    >
                                      Approve
                                    </Button>
                                  </span>
                                </Tooltip>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<CancelIcon />}
                                  disabled={busyId === r.id}
                                  onClick={() => review(r.id, 'reject')}
                                  sx={{
                                    borderColor: '#d32f2f',
                                    color: '#ef5350',
                                    fontWeight: 700,
                                    '&:hover': {
                                      borderColor: '#f44336',
                                      bgcolor: 'rgba(244,67,54,0.08)',
                                    },
                                  }}
                                >
                                  Reject
                                </Button>
                              </Stack>
                            ) : r.user_id || (r as { user_id?: string }).user_id ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  reissue(
                                    (r as { user_id?: string }).user_id || r.user_id!,
                                    r.user_email
                                  )
                                }
                                disabled={
                                  busyId === ((r as { user_id?: string }).user_id || r.user_id)
                                }
                                sx={{
                                  borderColor: 'rgba(215,245,173,.35)',
                                  color: '#edf5e8',
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  '&:hover': {
                                    borderColor: '#d7f5ad',
                                    bgcolor: 'rgba(215,245,173,.08)',
                                  },
                                }}
                              >
                                Re-issue key
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                      {requests.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            sx={{
                              color: '#aec7b3',
                              py: 4,
                              textAlign: 'center',
                              borderColor: 'rgba(215,245,173,0.15)',
                            }}
                          >
                            {requestFilter === 'pending'
                              ? 'No pending service requests.'
                              : 'No requests yet.'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label="Active keys (one per email)"
                  clickable
                  onClick={() => setKeyFilter('active')}
                  sx={{
                    bgcolor:
                      keyFilter === 'active' ? 'rgba(76,175,80,0.12)' : 'rgba(255,255,255,0.08)',
                    color: keyFilter === 'active' ? '#81c784' : '#aec7b3',
                    border:
                      keyFilter === 'active'
                        ? '1px solid rgba(76,175,80,0.25)'
                        : '1px solid rgba(255,255,255,0.15)',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label="All users (latest key each)"
                  clickable
                  onClick={() => setKeyFilter('all')}
                  sx={{
                    bgcolor:
                      keyFilter === 'all' ? 'rgba(215,245,173,0.12)' : 'rgba(255,255,255,0.08)',
                    color: keyFilter === 'all' ? '#d7f5ad' : '#aec7b3',
                    border:
                      keyFilter === 'all'
                        ? '1px solid rgba(215,245,173,0.25)'
                        : '1px solid rgba(255,255,255,0.15)',
                    fontWeight: 600,
                  }}
                />
              </Stack>
              <Typography variant="caption" sx={{ color: '#889e92', display: 'block', mb: 1.5 }}>
                Each email appears once. Re-issued keys no longer create duplicate rows.
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ borderColor: 'rgba(215,245,173,0.15)' }}>
                      <TableCell
                        sx={{
                          color: '#d7f5ad',
                          fontWeight: 700,
                          borderColor: 'rgba(215,245,173,0.15)',
                        }}
                      >
                        User (email)
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#d7f5ad',
                          fontWeight: 700,
                          borderColor: 'rgba(215,245,173,0.15)',
                        }}
                      >
                        Prefix
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#d7f5ad',
                          fontWeight: 700,
                          borderColor: 'rgba(215,245,173,0.15)',
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#d7f5ad',
                          fontWeight: 700,
                          borderColor: 'rgba(215,245,173,0.15)',
                        }}
                      >
                        Quota / day
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#d7f5ad',
                          fontWeight: 700,
                          borderColor: 'rgba(215,245,173,0.15)',
                        }}
                      >
                        Last used
                      </TableCell>
                      <TableCell
                        sx={{
                          color: '#d7f5ad',
                          fontWeight: 700,
                          borderColor: 'rgba(215,245,173,0.15)',
                        }}
                      >
                        Created
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: '#d7f5ad',
                          fontWeight: 700,
                          borderColor: 'rgba(215,245,173,0.15)',
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {keys.map((k) => (
                      <TableRow
                        key={k.id}
                        hover
                        sx={{ '&:hover': { bgcolor: 'rgba(215,245,173,0.04) !important' } }}
                      >
                        <TableCell sx={{ borderColor: 'rgba(215,245,173,0.15)', color: '#edf5e8' }}>
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#edf5e8' }}>
                            {k.user_email || '—'}
                          </Typography>
                          {(k.key_count ?? 0) > 1 && (
                            <Typography variant="caption" sx={{ color: '#889e92' }} display="block">
                              {k.key_count} keys total
                              {(k.revoked_count ?? 0) > 0 ? ` · ${k.revoked_count} revoked` : ''}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ borderColor: 'rgba(215,245,173,0.15)' }}>
                          <Box
                            component="code"
                            sx={{
                              color: '#d7f5ad',
                              bgcolor: 'rgba(215,245,173,0.1)',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.5,
                              fontFamily: 'monospace',
                            }}
                          >
                            {k.key_prefix}…
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderColor: 'rgba(215,245,173,0.15)' }}>
                          <Chip size="small" label={k.status} sx={statusChipSx(k.status)} />
                        </TableCell>
                        <TableCell sx={{ borderColor: 'rgba(215,245,173,0.15)', color: '#edf5e8' }}>
                          {k.rate_limit_per_day ?? 5000}
                        </TableCell>
                        <TableCell sx={{ borderColor: 'rgba(215,245,173,0.15)', color: '#b8cabd' }}>
                          {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell sx={{ borderColor: 'rgba(215,245,173,0.15)', color: '#b8cabd' }}>
                          {k.created_at ? new Date(k.created_at).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ borderColor: 'rgba(215,245,173,0.15)' }}>
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {k.status === 'active' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={busyId === k.id}
                                onClick={() => revoke(k.id)}
                                sx={{
                                  borderColor: '#d32f2f',
                                  color: '#ef5350',
                                  fontWeight: 700,
                                  textTransform: 'none',
                                  '&:hover': {
                                    borderColor: '#f44336',
                                    bgcolor: 'rgba(244,67,54,0.08)',
                                  },
                                }}
                              >
                                Revoke
                              </Button>
                            )}
                            {k.user_id && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={busyId === k.user_id}
                                onClick={() => reissue(k.user_id!, k.user_email)}
                                sx={{
                                  borderColor: 'rgba(215,245,173,.35)',
                                  color: '#edf5e8',
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  '&:hover': {
                                    borderColor: '#d7f5ad',
                                    bgcolor: 'rgba(215,245,173,.08)',
                                  },
                                }}
                              >
                                Re-issue
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {keys.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{
                            color: '#aec7b3',
                            py: 4,
                            textAlign: 'center',
                            borderColor: 'rgba(215,245,173,0.15)',
                          }}
                        >
                          No keys issued yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {tab === 2 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#edf5e8' }}>
                End-to-end workflow
              </Typography>
              <Typography
                component="ol"
                sx={{ pl: 2, color: '#b8cabd', lineHeight: 1.9, '& li': { mb: 1 } }}
              >
                <li>User signs in at geovera.in → Developers → DIGIPIN → submits use case.</li>
                <li>You open this admin panel → Pending → Approve (or Reject with note).</li>
                <li>
                  Copy the issued key from the dialog. User can also claim it once under Developers
                  → DIGIPIN.
                </li>
                <li>
                  User pastes key in QGIS DIGIPIN Encoder (Online mode). Plugin sends{' '}
                  <Box
                    component="code"
                    sx={{
                      color: '#d7f5ad',
                      bgcolor: 'rgba(215,245,173,0.1)',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontFamily: 'monospace',
                    }}
                  >
                    x-api-key
                  </Box>{' '}
                  to the Cloud DIGIPIN API.
                </li>
                <li>
                  digipin-api validates the key against GeoVera backend on every encode/decode
                  (cached ~5 minutes for speed).
                </li>
                <li>Revoke or Re-issue from the Keys tab if a key is lost or abused.</li>
              </Typography>
              <Button
                component={RouterLink}
                to="/developers/digipin"
                sx={{
                  mt: 2,
                  borderColor: 'rgba(215,245,173,.35)',
                  color: '#edf5e8',
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: '#d7f5ad',
                    bgcolor: 'rgba(215,245,173,.08)',
                  },
                }}
                variant="outlined"
              >
                Open user DIGIPIN page
              </Button>
            </Box>
          )}
        </Paper>
      </Container>

      <Dialog
        open={!!issuedKey}
        onClose={() => setIssuedKey(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0b201b',
            backgroundImage: 'none',
            border: '1px solid rgba(215,245,173,0.25)',
            color: '#edf5e8',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: '#edf5e8', fontWeight: 700 }}>
          API key issued — copy now
        </DialogTitle>
        <DialogContent sx={{ color: '#edf5e8' }}>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              bgcolor: 'rgba(255,152,0,0.12)',
              color: '#ffb74d',
              border: '1px solid rgba(255,152,0,0.25)',
              '& .MuiAlert-icon': { color: '#ffb74d' },
            }}
          >
            Share this key securely. The user can also claim it once from their DIGIPIN page (7
            days). After that it cannot be recovered — use Re-issue.
          </Alert>
          {issuedMeta && (
            <Typography variant="body2" sx={{ mb: 1.5, color: '#aec7b3' }}>
              Prefix:{' '}
              <Box
                component="code"
                sx={{
                  color: '#d7f5ad',
                  bgcolor: 'rgba(215,245,173,0.1)',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                }}
              >
                {issuedMeta}…
              </Box>
            </Typography>
          )}
          <Typography
            component="code"
            sx={{
              display: 'block',
              wordBreak: 'break-all',
              bgcolor: 'rgba(7,22,18,0.45)',
              border: '1px solid rgba(215,245,173,0.12)',
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: 13,
              color: '#edf5e8',
            }}
          >
            {issuedKey}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => issuedKey && navigator.clipboard.writeText(issuedKey)}
            sx={{
              color: '#d7f5ad',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(215,245,173,0.08)' },
            }}
          >
            Copy key
          </Button>
          <Button
            variant="contained"
            onClick={() => setIssuedKey(null)}
            sx={{
              bgcolor: '#d7f5ad',
              color: '#081713',
              fontWeight: 800,
              textTransform: 'none',
              '&:hover': { bgcolor: '#fff' },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DigipinApiKeysAdminPage;
